import { lazy, Suspense, useEffect, useState } from "react";
import type { ActivityItem, BoostSummary, LaunchSummary, ProtocolStats } from "@bubblefun/shared";
import { compact, compactUsd } from "../lib/format";
import { getLiveActivity } from "../lib/api";
import { LiveActivityTicker } from "../components/LiveActivityTicker";
import { MarketBrowser } from "../components/MarketBrowser";
import { BoostHeroSpotlight } from "../components/BoostHeroSpotlight";

const ExternalMarkets = lazy(() => import("../components/ExternalMarkets"));

const heroBackgroundBubbles = [
  ["6%", "38px", "44px", "-2s", "8s"],
  ["16%", "64px", "-52px", "-7s", "11s"],
  ["27%", "24px", "66px", "-1s", "7s"],
  ["37%", "48px", "-38px", "-8s", "12s"],
  ["47%", "20px", "30px", "-4s", "7.5s"],
  ["57%", "72px", "58px", "-10s", "13s"],
  ["67%", "32px", "-56px", "-3s", "9s"],
  ["77%", "54px", "42px", "-9s", "11.5s"],
  ["87%", "26px", "-26px", "-5s", "8s"],
  ["95%", "42px", "-68px", "-11s", "12.5s"],
  ["2%", "28px", "48px", "-6s", "10s"],
  ["52%", "16px", "-22px", "-2.5s", "6.5s"]
] as const;

const slides = [
  { number: "01", title: "Write the market", copy: "Name the token, add immutable metadata, choose an optional creator buy, and preview its deterministic b0b address before signing.", note: "One launch transaction" },
  { number: "02", title: "Price it once", copy: "One billion tokens enter a single-sided Uniswap V3 position at a 1.5 ETH starting FDV. There is no private inventory allocation.", note: "1% V3 fee tier" },
  { number: "03", title: "Lock the position", copy: "The position NFT moves directly into BubbleLocker. Its interface has no release or decrease-liquidity path.", note: "Permanent custody" },
  { number: "04", title: "Let the market work", copy: "Creators receive 35% of WETH-side trading fees. At 3.7 WETH the market earns a visible milestone—without moving liquidity.", note: "Permissionless collection" }
];

export default function HomePage({ launches, externalMarkets, boosts, stats, demo, boostDemo, onLaunch }: { launches: LaunchSummary[]; externalMarkets: LaunchSummary[]; boosts: BoostSummary[]; stats: ProtocolStats; demo: boolean; boostDemo: boolean; onLaunch(): void }) {
  const [activity, setActivity] = useState<ActivityItem[]>([]);

  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      const next = await getLiveActivity();
      if (!disposed) setActivity(next);
    };
    const onVisibilityChange = () => { if (document.visibilityState === "visible") void refresh(); };
    void refresh();
    const interval = window.setInterval(() => { if (document.visibilityState === "visible") void refresh(); }, 4_000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => { disposed = true; window.clearInterval(interval); document.removeEventListener("visibilitychange", onVisibilityChange); };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible")), { threshold: 0.12 });
    const motionObserver = new IntersectionObserver((entries) => entries.forEach((entry) => entry.target.classList.toggle("is-running", entry.isIntersecting)), { threshold: 0.08 });
    document.querySelectorAll("[data-reveal]").forEach((element) => observer.observe(element));
    document.querySelectorAll(".mechanism-marquee, .market-ticker, .home-hero-atmosphere").forEach((element) => motionObserver.observe(element));
    return () => { observer.disconnect(); motionObserver.disconnect(); };
  }, []);

  return (
    <main>
      <div className={`hero-stage${demo ? " hero-stage--demo" : ""}`}>
        {demo && <div className="demo-banner"><b>Interface preview</b><span>Market figures below are labeled demonstration data until the D1 indexer is configured.</span></div>}
        <section className="hero">
          <div className="home-hero-atmosphere is-running" aria-hidden="true">
            <span className="home-hero-stars">{Array.from({ length: 24 }, (_, index) => <i key={index} style={{ left: `${(index * 37) % 94 + 3}%`, top: `${(index * 53) % 68 + 5}%`, animationDelay: `${index * -0.29}s` }} />)}</span>
            <span className="home-hero-bubble-stream">{heroBackgroundBubbles.map(([left, size, drift, delay, duration], index) => <i key={index} style={{ left, width: size, height: size, animationDelay: delay, animationDuration: duration, "--bubble-drift": drift } as React.CSSProperties} />)}</span>
            <span className="home-hero-grid" />
            <span className="home-hero-orbit home-hero-orbit--outer"><i /><i /><i /></span>
            <span className="home-hero-orbit home-hero-orbit--inner"><i /><i /></span>
            <span className="home-hero-bubble home-hero-bubble--one">{launches[0]?.symbol.slice(0, 2) || "PL"}</span>
            <span className="home-hero-bubble home-hero-bubble--two" />
            <span className="home-hero-bubble home-hero-bubble--three">{launches[2]?.symbol.slice(0, 2) || "GR"}</span>
            <span className="home-hero-bubble home-hero-bubble--four">{launches[1]?.symbol.slice(0, 2) || "TH"}</span>
            <span className="home-hero-floor" />
          </div>
          <div className="hero-copy">
            <span className="hero-chain-status"><i /> Robinhood Chain live</span>
            <h1>Fair launch,<br /><em>from block one.</em></h1>
            <p>Create a fixed-supply token with permanent liquidity and transparent market rules on <strong>Robinhood Chain.</strong></p>
            <div className="hero-actions"><button className="button button--primary" type="button" onClick={onLaunch}>Launch token <span>↗</span></button><a className="button button--secondary" href="#open-markets">View markets <span>→</span></a></div>
            <div className="hero-proof" aria-label="Protocol guarantees">
              <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3M5 10h14v11H5z" /></svg>Locked liquidity</span>
              <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2 20 5v6c0 5-3.2 9.1-8 11-4.8-1.9-8-6-8-11V5zM9 12l2 2 4-5" /></svg>Fixed supply</span>
              <span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5-6 7 6 7m8-14 6 7-6 7m-2-17-4 20" /></svg>Transparent rules</span>
            </div>
          </div>
        </section>
        <BoostHeroSpotlight launches={launches} boosts={boosts} preview={boostDemo} />
        <LiveActivityTicker activity={activity} launches={launches} />
      </div>

      <Suspense fallback={<section className="rpc-markets"><span className="eyebrow">Loading verified Robinhood markets…</span></section>}><ExternalMarkets markets={externalMarkets} /></Suspense>

      <section className="market-section market-section--home" id="open-markets" data-reveal>
        <div className="market-section-head"><h2>Discover</h2></div>
        <MarketBrowser launches={launches} />
      </section>

      <section className="protocol-stats" data-reveal>
        <span className="section-index">Protocol / current</span>
        <dl><div><dt>Markets created</dt><dd>{compact(stats.launches, 0)}</dd></div><div><dt>Indexed volume</dt><dd>{compactUsd(stats.volumeUsd)}</dd></div><div><dt>Creator fees</dt><dd>{stats.creatorFeesEth.toFixed(2)} <small>ETH</small></dd></div><div><dt>Locked positions</dt><dd>{compact(stats.lockedPositions, 0)}</dd></div></dl>
      </section>

      <section className="mechanism-section" data-reveal>
        <div className="section-heading"><span className="eyebrow">The mechanism</span><h2>Four moves.<br />No hidden second act.</h2></div>
        <div className="mechanism-marquee" aria-label="How Bubble Hood works" tabIndex={0}>
          <div className="mechanism-slides">
            {[0, 1].map((cycle) => (
              <div className="mechanism-sequence" key={cycle} aria-hidden={cycle === 1 ? true : undefined}>
                {slides.map((slide) => <article key={`${cycle}-${slide.number}`}><span className="slide-number">{slide.number}</span><div><h3>{slide.title}</h3><p>{slide.copy}</p></div><span className="slide-note">{slide.note}</span><i aria-hidden="true" /></article>)}
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
